/**
 * Multi-Hierarchy Details table — request/response contract (BUD-18987).
 *
 * `POST /api/reporting_service/next/pivot/details` lists the rows of one column group's lens that sit
 * under the report's row hierarchy: the hierarchy path each row belongs to, the account's
 * chart-of-accounts columns, and the lens columns as-is. Paging, sorting, filtering, grouping and
 * totals run in SQL. The report's own filters (report, row-group, the selected column group's filters
 * and date filter, and the as-of date) restrict the rows, so summing a measure column over the
 * Details rows reproduces the Summary grid's figure for that column group.
 *
 * The persisted half of the feature (what a saved view remembers) lives in
 * `./accountSummary/multiHierarchyDetails`.
 */
import {
  ColumnValue,
  MhDetailsCell,
  MhDetailsFilter,
  MhDetailsGroupBy,
  MhDetailsSort,
  MultiHierarchyAccountSummary
} from './accountSummary';

export type MhDetailsExportMode = 'records' | 'groupedSubtotals';

export interface MhDetailsArgs {
  report: MultiHierarchyAccountSummary;
  /** Which column group's lens to list. Defaults to the first data column group. */
  columnGroupId?: string;
  /**
   * The columns the user has visible, in display order. Drives the SQL projection (the server adds
   * any column a filter, sort or group-by needs) and, on export, exactly which columns are written
   * and in which order. Absent means the server's default visible set.
   */
  visibleColumnKeys?: string[];
  /** Group By. When set without `groupKeys`, the response is one group row per distinct value. */
  groupBy?: MhDetailsGroupBy;
  /**
   * The group being expanded — one key per `groupBy` level, blank values as
   * `MH_DETAILS_BLANK_GROUP_KEY`. When set, the response is that group's child rows.
   */
  groupKeys?: string[];
  limit?: number;
  offset?: number;
  sort?: MhDetailsSort[];
  filters?: MhDetailsFilter[];
  /** Adds `metadata.totals` — one totals row per row group, over the whole filtered set. */
  showTotals?: boolean;
  /** Export only. `groupedSubtotals` writes one row per group; ignored when not grouped. */
  exportMode?: MhDetailsExportMode;
  /** Adds `metadata.queries`, the SQL of every statement the request ran, for debugging. */
  includeQueries?: boolean;
  entitySubdomain?: string;
  entityFiscalYearStartMonth?: number;
  entityTimezone?: string | null;
  reportConfigurationId?: string;
  /**
   * Accepted and ignored for one release: the table no longer pivots, so it has no use for the
   * Summary grid's column values. Remove once no deployed client sends it.
   */
  columnValues?: ColumnValue[];
  /**
   * The entity group whose COMBINED chart of accounts to resolve, for a combined report.
   *
   * Details resolves the rollup and code-map dimensions itself (`resolveDetailsSources`), so it
   * needs the same CoA source the Summary grid uses or its leaf accounts hang off the wrong
   * hierarchy. Authorized server-side on every request, like the pivot args beside it.
   */
  entityGroupId?: string;
}

/**
 * - `rowGroup` — the row group's label, present when it tells rows apart
 * - `hierarchy` — one level of the row hierarchy at one depth below its root
 * - `coa` — the account string, or one ERP segment's code or description
 * - `lens` — a column of the lens, as-is
 * - `groupCount` — a group row's child count
 */
export type MhDetailsColumnKind = 'rowGroup' | 'hierarchy' | 'coa' | 'lens' | 'groupCount';

/**
 * How a column's values should be read and formatted, normalized by the server from the lens's
 * logical type and semantic types so the client never parses type urns. `integer` covers
 * zero-scale numerics such as fiscal year and period, which arrive as `numeric(19,4)`.
 */
export type MhDetailsDataType = 'text' | 'number' | 'integer' | 'currency' | 'date' | 'boolean';

export interface MhDetailsColumnMeta {
  /** Stable key. Filters, sort, group-by and saved column layouts refer to columns by it. */
  key: string;
  /** `Row Group`, `RowGroup 2.1`, `Account`, `Fund Code`, or the lens column's label. */
  label: string;
  kind: MhDetailsColumnKind;
  dataType: MhDetailsDataType;
  isNumeric: boolean;
  /** Hierarchy only: 1-based configured level. */
  position?: number;
  /** Hierarchy only: 1-based depth below the level's root (the root itself is 1). */
  depth?: number;
  /** Hierarchy only: the ERP segment name, surfaced as a column tooltip. */
  segmentName?: string;
  semanticTypes?: string[];
  /** Listed in Manage Columns but not shown until the user asks for it. */
  defaultHidden?: boolean;
}

export type MhDetailsTruncationReason = 'exportCap';

/**
 * The SQL of each statement a Details request ran, as the query service echoed it. `groupCount` runs
 * for group rows only and `totals` when totals were requested, so each is present only when it ran.
 */
export interface MhDetailsQueries {
  /** The page: lens rows, or group rows while grouped. */
  page: string;
  /** The row count. */
  count: string;
  /** The presence counts that decide which columns exist. */
  presence?: string;
  groupCount?: string;
  totals?: string;
}

/**
 * Response header on `POST /next/pivot/details/export` naming why the CSV stops short, when it does.
 * The body is the CSV itself, so this is the only way the export can say it was capped.
 */
export const MH_DETAILS_TRUNCATION_HEADER = 'X-OG-Details-Truncated';

/** A Details CSV export, and why it stops short when it does. */
export interface MhDetailsExportResult {
  csv: string;
  truncationReason?: MhDetailsTruncationReason;
}

export type MhDetailsOmittedRowGroupReason = 'calculated' | 'lensMismatch';

export interface MhDetailsOmittedRowGroup {
  /** Ordinal in `report.rowGroups`. */
  index: number;
  label: string;
  reason: MhDetailsOmittedRowGroupReason;
}

export interface MhDetailsColumnGroupOption {
  id: string;
  label: string;
  lensId: string;
}

export interface MhDetailsResult {
  /** Positional rows aligned to `metadata.columns`, matching the pivot convention. */
  data: MhDetailsCell[][];
  metadata: {
    /** The columns `data` carries, in order. Group rows carry the group column, sums and count. */
    columns: MhDetailsColumnMeta[];
    /**
     * Every column the table can show, whether fetched or not — what Manage Columns lists. Hierarchy
     * and segment columns empty across the report's whole scope are left out (grid filters do not
     * count, so filtering never removes a column).
     */
    availableColumns: MhDetailsColumnMeta[];
    /**
     * Columns left out of `availableColumns` only because they are empty in the current scope. They
     * still exist, so a saved setting naming one is kept rather than pruned. Present only when non-empty.
     */
    emptyColumnKeys?: string[];
    /** Rows (or groups, for a group-row response) matching the current filters, before paging. */
    totalRowCount: number;
    /**
     * The count ran past its time budget, so `totalRowCount` is the rows seen up to the end of this
     * page: exact on a short last page, a lower bound otherwise.
     */
    isRowCountEstimate?: boolean;
    /** Group-row responses only: the lens rows under all the groups, which is what a records export writes. */
    recordCount?: number;
    /** True when `data` holds group rows rather than lens rows. */
    isGroupRows?: boolean;
    /**
     * Present when `showTotals` was requested. Rows aligned to `columns`, one per row group, summed
     * over the whole filtered set rather than the page, and labelled `Total` (in `row_group_index` order).
     *
     * Totalled **per row group** on purpose: an account can belong to more than one row group's
     * scope and correctly appears under each, so a single grand total would double-count it.
     */
    totals?: MhDetailsCell[][];
    /** The column group whose lens the rows come from, and the choices when there are several lenses. */
    columnGroupId: string;
    lensId: string;
    availableColumnGroups: MhDetailsColumnGroupOption[];
    /** Row groups the table does not list, so the UI can say so. Present only when non-empty. */
    omittedRowGroups?: MhDetailsOmittedRowGroup[];
    truncated?: boolean;
    truncationReason?: MhDetailsTruncationReason;
    /** Effective limit after server-side clamping, when it differs from the requested one. */
    effectiveLimit?: number;
    /** Present only when `includeQueries` was requested. */
    queries?: MhDetailsQueries;
  };
}
